<template>
  <transition name="fs-fade">
    <div v-if="localVisible" class="oim-fullscreen">
      <!-- Header oscuro fullscreen -->
      <div class="oim-header">
        <div class="oim-header-left">
          <button class="oim-back" @click="handleClose">
            <i class="el-icon-arrow-left"></i>
          </button>
          <h5 class="oim-title">Facturas vencidas</h5>
          <span v-if="invoices.length" class="oim-count-badge">{{
            invoices.length
          }}</span>
        </div>
        <div class="oim-header-right">
          <button class="oim-close" @click="handleClose">
            <i class="el-icon-close"></i>
          </button>
        </div>
      </div>

      <!-- Filtros -->
      <div class="oim-filters">
        <el-input
          v-model="search"
          placeholder="Buscar por cliente o codigo..."
          suffix-icon="el-icon-search"
          size="small"
          clearable
          class="oim-search"
        />
        <div class="oim-tabs">
          <button
            v-for="opt in daysOptions"
            :key="opt.value"
            :class="['oim-tab', { active: filterDays === opt.value }]"
            @click="
              filterDays = opt.value;
              onFilterChange();
            "
          >
            {{ opt.label }}
          </button>
        </div>
        <el-select
          v-model="filterMinAmount"
          placeholder="Monto minimo"
          size="small"
          class="oim-select"
          :class="{ 'oim-select--active': filterMinAmount > 0 }"
          @change="onFilterChange"
        >
          <el-option label="Todos" :value="0" />
          <el-option label="+$100" :value="100" />
          <el-option label="+$500" :value="500" />
          <el-option label="+$1000" :value="1000" />
        </el-select>
        <button
          class="oim-sort-btn"
          @click="toggleSort"
          :title="sortAsc ? 'Menor a mayor' : 'Mayor a menor'"
        >
          <i :class="sortAsc ? 'el-icon-sort-up' : 'el-icon-sort-down'"></i>
          <span>{{ sortAsc ? "Menor" : "Mayor" }}</span>
        </button>
      </div>

      <!-- Body scrollable -->
      <div class="oim-body">
        <!-- Skeleton -->
        <div v-show="loading" class="oim-skeleton">
          <div v-for="n in 8" :key="n" class="oim-skeleton-row">
            <div class="sk-check"></div>
            <div class="sk-line w20"></div>
            <div class="sk-line w30"></div>
            <div class="sk-line w15"></div>
            <div class="sk-line w15"></div>
          </div>
        </div>

        <!-- Tabla -->
        <div v-show="!loading && readyToRender" class="oim-table-wrap">
          <div class="oim-table-header">
            <el-checkbox
              :indeterminate="isIndeterminate"
              :value="allSelected"
              @change="toggleSelectAll"
            />
            <span>Codigo</span>
            <span>Cliente</span>
            <span>Monto</span>
            <span>Dias vencida</span>
            <span>Acciones</span>
          </div>

          <div
            v-for="inv in paginatedInvoices"
            :key="inv.id"
            :class="[
              'oim-table-row',
              { 'is-selected': selectedIds.includes(inv.id) }
            ]"
          >
            <el-checkbox
              :value="selectedIds.includes(inv.id)"
              @change="v => toggleSelect(inv.id, v)"
            />
            <a class="oim-code-link" @click="goToInvoice(inv)">{{
              inv.code
            }}</a>
            <span class="oim-client-text">{{ inv.client_name }}</span>
            <span class="oim-amount-text">${{ formatMoney(inv.total) }}</span>
            <span>
              <span :class="['oim-days-badge', daysClass(inv.days_overdue)]">
                {{ inv.days_overdue }} dias
              </span>
            </span>
            <div class="oim-row-actions">
              <button
                class="row-icon-btn row-icon-btn--teal"
                title="Enviar recordatorio"
                @click="rowSendReminder(inv)"
              >
                <i class="el-icon-bell"></i>
              </button>
              <button
                class="row-icon-btn row-icon-btn--green"
                title="Marcar cobrada"
                @click="markPaid(inv)"
              >
                <i class="el-icon-check"></i>
              </button>
              <button
                class="row-icon-btn row-icon-btn--danger"
                title="Anular factura"
                @click="annulInvoice(inv)"
              >
                <i class="el-icon-close"></i>
              </button>
            </div>
          </div>

          <div v-if="hasMore" class="oim-load-more">
            <button class="oim-load-more-btn" @click="displayLimit += 50">
              Mostrar más ({{ filteredInvoices.length - displayLimit }}
              restantes)
            </button>
          </div>

          <div v-if="!filteredInvoices.length" class="oim-no-results">
            <i class="el-icon-search"></i>
            <span>Sin resultados con los filtros aplicados</span>
          </div>
        </div>

        <!-- Empty -->
        <div
          v-show="!loading && !invoices.length && readyToRender"
          class="oim-empty"
        >
          <i class="el-icon-circle-check"></i>
          <span>No hay facturas vencidas</span>
        </div>
      </div>

      <!-- Barra de acciones masivas -->
      <transition name="toolbar-slide">
        <div v-if="selectedIds.length" class="oim-bulk-bar">
          <span class="oim-bulk-info">
            {{ selectedIds.length }} factura{{
              selectedIds.length !== 1 ? "s" : ""
            }}
            seleccionada{{ selectedIds.length !== 1 ? "s" : "" }}
          </span>
          <div class="oim-bulk-actions">
            <el-button
              size="small"
              class="oim-bulk-btn"
              @click="openBulkReminder"
            >
              <i class="el-icon-chat-dot-round"></i> Recordatorio ({{
                selectedIds.length
              }})
            </el-button>
            <el-button
              size="small"
              class="oim-bulk-btn-secondary"
              @click="bulkMarkPaid"
            >
              <i class="el-icon-check"></i> Marcar cobradas ({{
                selectedIds.length
              }})
            </el-button>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import batchActionsMixin from "@/assets/mixins/batchActionsMixin";
import dashboardService from "../services/dashboardService";
import invoiceService from "../../sales/pages/invoices/services/invoiceService";

export default {
  name: "OverdueInvoicesModal",
  mixins: [batchActionsMixin],
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      readyToRender: false,
      invoices: [],
      search: "",
      filterDays: 0,
      filterMinAmount: 0,
      sortAsc: false,
      selectedIds: [],
      displayLimit: 50,
      daysOptions: [
        { label: "Todas", value: 0, max: null },
        { label: "1–14 dias", value: 1, max: 14 },
        { label: "15–29 dias", value: 15, max: 29 },
        { label: "30–59 dias", value: 30, max: 59 },
        { label: "60–89 dias", value: 60, max: 89 },
        { label: "+90 dias", value: 90, max: null }
      ]
    };
  },
  computed: {
    localVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      }
    },
    filteredInvoices() {
      let list = this.invoices;
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(
          r =>
            (r.code || "").toLowerCase().includes(q) ||
            (r.client_name || "").toLowerCase().includes(q)
        );
      }
      if (this.filterDays > 0) {
        const opt = this.daysOptions.find(o => o.value === this.filterDays);
        const max = opt ? opt.max : null;
        list = list.filter(
          r =>
            r.days_overdue >= this.filterDays &&
            (max === null || r.days_overdue <= max)
        );
      }
      if (this.filterMinAmount > 0) {
        list = list.filter(r => r.total >= this.filterMinAmount);
      }
      list = [...list].sort((a, b) =>
        this.sortAsc
          ? a.days_overdue - b.days_overdue
          : b.days_overdue - a.days_overdue
      );
      return list;
    },
    paginatedInvoices() {
      return this.filteredInvoices.slice(0, this.displayLimit);
    },
    hasMore() {
      return this.displayLimit < this.filteredInvoices.length;
    },
    allSelected() {
      return (
        this.filteredInvoices.length > 0 &&
        this.filteredInvoices.every(i => this.selectedIds.includes(i.id))
      );
    },
    isIndeterminate() {
      const count = this.filteredInvoices.filter(i =>
        this.selectedIds.includes(i.id)
      ).length;
      return count > 0 && count < this.filteredInvoices.length;
    }
  },
  watch: {
    search() {
      this.displayLimit = 50;
    },
    filterDays() {
      this.displayLimit = 50;
    },
    filterMinAmount() {
      this.displayLimit = 50;
    },
    visible(val) {
      if (val) {
        this.loadInvoices();
        document.addEventListener("keydown", this.onEscape);
        document.body.style.overflow = "hidden";
      } else {
        document.removeEventListener("keydown", this.onEscape);
        document.body.style.overflow = "";
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEscape);
    document.body.style.overflow = "";
  },
  methods: {
    onFilterChange() {
      this.loadInvoices();
    },
    toggleSort() {
      this.sortAsc = !this.sortAsc;
    },
    async loadInvoices() {
      this.loading = true;
      this.readyToRender = false;
      this.selectedIds = [];
      this.displayLimit = 50;
      try {
        const res = await dashboardService.getOverdueInvoices({
          params: {
            search: this.search,
            days: this.filterDays || 0,
            min_amount: this.filterMinAmount || 0
          }
        });

        if (res && res.data && res.data.success) {
          this.invoices = res.data.data || [];
        } else if (res && res.data) {
          this.invoices = [];
          const errorMsg =
            res.data.message || "Error al cargar facturas vencidas";
          this.$message.error(errorMsg);
        } else {
          this.invoices = [];
          this.$message.error("Error: respuesta inválida del servidor");
        }
      } catch (e) {
        this.invoices = [];
        this.$message.error(
          "Error al cargar facturas vencidas: " + (e.message || "desconocido")
        );
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          setTimeout(() => {
            this.readyToRender = true;
          }, 50);
        });
      }
    },
    toggleSelectAll(val) {
      if (val) {
        const ids = this.filteredInvoices.map(i => i.id);
        this.selectedIds = [...new Set([...this.selectedIds, ...ids])];
      } else {
        const filteredIds = this.filteredInvoices.map(i => i.id);
        this.selectedIds = this.selectedIds.filter(
          id => !filteredIds.includes(id)
        );
      }
    },
    toggleSelect(id, val) {
      if (val) {
        if (!this.selectedIds.includes(id))
          this.selectedIds = [...this.selectedIds, id];
      } else {
        this.selectedIds = this.selectedIds.filter(i => i !== id);
      }
    },
    async markPaid(inv) {
      try {
        await this.$confirm(
          `Marcar factura ${inv.code} como cobrada?`,
          "Confirmar",
          {
            confirmButtonText: "Si, cobrada",
            cancelButtonText: "Cancelar",
            type: "info"
          }
        );
        await invoiceService.invoicePaid({
          user_business_invoice_id: inv.id,
          is_paid: true
        });
        this.invoices = this.invoices.filter(i => i.id !== inv.id);
        this.selectedIds = this.selectedIds.filter(id => id !== inv.id);
        this.$message.success(`Factura ${inv.code} marcada como cobrada`);
        this.$emit("invoices-updated");
      } catch {
        /* Cancelado */
      }
    },
    async bulkMarkPaid() {
      const count = this.selectedIds.length;
      try {
        await this.$confirm(
          `Marcar ${count} factura${count !== 1 ? "s" : ""} como cobrada${
            count !== 1 ? "s" : ""
          }?`,
          "Confirmar",
          {
            confirmButtonText: "Si, continuar",
            cancelButtonText: "Cancelar",
            type: "info"
          }
        );

        // Usar batch processing para marcar múltiples como pagadas
        await this.executeBatchAction({
          title: "Marcando facturas como cobradas",
          items: this.selectedIds,
          processor: batch => invoiceService.invoicePaidBatch(batch, true),
          batchSize: 5,
          onSuccess: () => {
            this.invoices = this.invoices.filter(
              i => !this.selectedIds.includes(i.id)
            );
            this.selectedIds = [];
            this.$message.success(
              `${count} factura${count !== 1 ? "s" : ""} cobrada${
                count !== 1 ? "s" : ""
              }`
            );
            this.$emit("invoices-updated");
          }
        });
      } catch {
        /* Cancelado */
      }
    },
    goToInvoice(inv) {
      this.$root.$emit("preview-invoice", inv.id);
    },
    rowSendReminder(inv) {
      this.$emit("bulk-reminder", {
        recipients: [{ id: inv.id, code: inv.code }]
      });
    },
    openBulkReminder() {
      this.$emit("bulk-reminder", { recipients: [] });
    },
    annulInvoice(inv) {
      this.$message.info("Anular factura: " + inv.code);
    },
    formatMoney(val) {
      return new Intl.NumberFormat("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val || 0);
    },
    daysClass(days) {
      if (days >= 60) return "days-red";
      if (days >= 30) return "days-amber";
      return "days-yellow";
    },
    handleClose() {
      this.$emit("update:visible", false);
    },
    onEscape(e) {
      if (e.key === "Escape") this.handleClose();
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$dark-header: #1e1c42;
$accent: #3ecfb4;
$border: #e4eaf6;
$bg: #f7f8f9;

// ── FULLSCREEN MODAL ──
.oim-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #fff;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// Header limpio
.oim-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #e4eaf6;
  flex-shrink: 0;
}
.oim-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.oim-back {
  width: 36px;
  height: 36px;
  border: none;
  background: #f0f1f5;
  border-radius: 8px;
  color: $primary;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover {
    background: #e4eaf6;
  }
}
.oim-title {
  font-size: 16px;
  font-weight: 600;
  color: $primary;
  margin: 0;
}
.oim-count-badge {
  background: #e24b4a;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}
.oim-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.oim-close {
  width: 36px;
  height: 36px;
  border: none;
  background: #f0f1f5;
  border-radius: 8px;
  color: $primary;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover {
    background: #e4eaf6;
  }
}

// Filtros
.oim-filters {
  display: flex;
  gap: 12px;
  padding: 14px 24px;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: center;
  background: #fff;
}
.oim-search {
  flex: 1;
  min-width: 180px;
  max-width: 320px;
}
.oim-tabs {
  display: flex;
  border: 1px solid $border;
  border-radius: 6px;
  overflow: hidden;
}
.oim-tab {
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  background: #fff;
  border: none;
  cursor: pointer;
  border-right: 1px solid $border;
  transition: all 0.15s;
  &:last-child {
    border-right: none;
  }
  &:hover {
    background: $bg;
  }
  &.active {
    background: $primary;
    color: #fff;
  }
}
.oim-select {
  width: 130px;

  &--active {
    ::v-deep .el-input__inner {
      color: #2d2b55 !important;
      font-weight: 600;
    }
  }
}
.oim-sort-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border: 1px solid $border;
  border-radius: 6px;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  i {
    font-size: 14px;
  }
  &:hover {
    background: $bg;
    color: $primary;
    border-color: $primary;
  }
}

// Body
.oim-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

// Table
.oim-table-header,
.oim-table-row {
  ::v-deep .el-checkbox {
    margin: 0;
    padding: 0;
    line-height: 1;
    .el-checkbox__label {
      display: none;
    }
  }
}
.oim-table-header {
  display: grid;
  grid-template-columns: 32px 120px 1fr 120px 120px 116px;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  background: #fff;
  border-bottom: 2px solid $border;
  font-size: 12px;
  font-weight: 700;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  position: sticky;
  top: 0;
  z-index: 5;
}
.oim-table-row {
  display: grid;
  grid-template-columns: 32px 120px 1fr 120px 120px 116px;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  border-bottom: 1px solid $border;
  font-size: 14px;
  min-height: 56px;
  transition: background 0.12s;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: $bg;
  }
  &.is-selected {
    background: #f0f4ff;
  }
}
.oim-code-text {
  font-weight: 600;
  color: $primary;
  font-size: 14px;
}
.oim-code-link {
  font-weight: 600;
  color: $primary;
  font-size: 14px;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: #3ecfb4;
  }
}
.oim-client-text {
  color: #303133;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.oim-amount-text {
  font-weight: 700;
  color: $primary;
  font-size: 14px;
}
.oim-days-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
  &.days-red {
    background: #fff0f0;
    color: #e24b4a;
  }
  &.days-amber {
    background: #fff8e6;
    color: #ba7517;
  }
  &.days-yellow {
    background: #fefce8;
    color: #b45309;
  }
}
.oim-row-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

// Icon-only action buttons
.row-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e4eaf6;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s;
  flex-shrink: 0;

  &--teal {
    color: $accent;
    &:hover {
      background: rgba($accent, 0.08);
      border-color: $accent;
    }
  }
  &--green {
    color: #1d9e75;
    &:hover {
      background: rgba(#1d9e75, 0.08);
      border-color: #1d9e75;
    }
  }
  &--danger {
    color: #909399;
    &:hover {
      background: #fff0f0;
      border-color: #e24b4a;
      color: #e24b4a;
    }
  }
}
.oim-load-more {
  display: flex;
  justify-content: center;
  padding: 20px;
}
.oim-load-more-btn {
  padding: 10px 32px;
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  background: #f0f1f5;
  border: 1px solid $border;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: $accent;
    color: #fff;
    border-color: $accent;
  }
}
.oim-no-results {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 40px;
  color: #c0c4cc;
  font-size: 13px;
  justify-content: center;
}
.oim-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: #c0c4cc;
  gap: 12px;
  i {
    font-size: 42px;
    color: $accent;
  }
  span {
    font-size: 14px;
    font-weight: 600;
    color: #909399;
  }
}

// Bulk bar
.oim-bulk-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: #fff;
  border-top: 1px solid $border;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 10px;
}
.oim-bulk-info {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
}
.oim-bulk-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.oim-bulk-btn {
  font-size: 12px;
  background: $accent;
  border: 1px solid $accent;
  color: #fff;
  border-radius: 8px;
  &:hover,
  &:focus {
    background: darken($accent, 5%);
    border-color: darken($accent, 5%);
    color: #fff;
  }
}
.oim-bulk-btn-secondary {
  font-size: 12px;
  background: #fff;
  border: 1px solid #dcdfe6;
  color: #606266;
  border-radius: 8px;
  &:hover,
  &:focus {
    border-color: #909399;
    color: $primary;
  }
}

// Transitions
.toolbar-slide-enter-active,
.toolbar-slide-leave-active {
  transition: max-height 0.25s, opacity 0.25s;
  overflow: hidden;
  max-height: 80px;
}
.toolbar-slide-enter,
.toolbar-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.fs-fade-enter-active,
.fs-fade-leave-active {
  transition: opacity 0.2s;
}
.fs-fade-enter,
.fs-fade-leave-to {
  opacity: 0;
}

// Skeleton
.oim-skeleton {
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.oim-skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.sk-check {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  flex-shrink: 0;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
.sk-line {
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  &.w15 {
    width: 15%;
  }
  &.w20 {
    width: 20%;
  }
  &.w30 {
    width: 30%;
  }
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 768px) {
  .oim-table-header {
    display: none !important;
  }
  .oim-table-row {
    display: flex !important;
    flex-wrap: wrap;
    gap: 6px 12px;
    padding: 16px;
    font-size: 14px;

    // Hide checkbox on mobile

    .oim-code-text {
      font-size: 13px;
      color: #909399;
    }
    .oim-client-text {
      flex-basis: 100%;
      order: -2;
      font-size: 15px;
      font-weight: 600;
    }
    .oim-amount-text {
      font-size: 15px;
    }
  }
  .oim-row-actions {
    width: 100% !important;
    flex-basis: 100% !important;
    display: flex !important;
    margin-top: 8px;
    gap: 8px;

    .el-button {
      flex: 1;
      padding: 10px 8px !important;
      font-size: 13px !important;
      min-height: 40px;
    }
  }
  .oim-filters {
    flex-direction: column;
    gap: 8px;
    .oim-search {
      max-width: 100%;
    }
    .oim-tabs {
      width: 100%;
      overflow-x: auto;
    }
  }
}
</style>
